import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  DatePicker,
  Row,
  Col,
  InputNumber,
  Drawer,
  Divider,
  Tooltip,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  BookOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const VOUCHER_TYPES = [
  'Payment',
  'Receipt',
  'Journal',
  'Contra',
  'Sales',
  'Purchase',
  'Bank',
  'Sales Return',
];

const emptyRow = (index) => ({
  key: `row-${Date.now()}-${index}`,
  miti_bs: '',
  date_ad: null,
  vch_type: null,
  vch_no: '',
  particulars: '',
  debit: 0,
  credit: 0,
});

const Ledgers = () => {
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [editingLedger, setEditingLedger] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const [entries, setEntries] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const queryClient = useQueryClient();

  const {
    data: ledgersData,
    isLoading,
    refetch,
  } = useQuery(
    ['ledgers', searchText],
    async () => {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      params.append('limit', '100');
      const response = await api.get(`/admin/ledgers?${params.toString()}`);
      return response.data.data;
    },
    { keepPreviousData: true },
  );

  const createLedger = useMutation((payload) => api.post('/admin/ledgers', payload), {
    onSuccess: () => {
      message.success('Ledger saved successfully.');
      queryClient.invalidateQueries(['ledgers']);
      closeDrawer();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to save ledger.');
    },
  });

  const updateLedger = useMutation(({ id, payload }) => api.put(`/admin/ledgers/${id}`, payload), {
    onSuccess: () => {
      message.success('Ledger updated successfully.');
      queryClient.invalidateQueries(['ledgers']);
      closeDrawer();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to update ledger.');
    },
  });

  const deleteLedger = useMutation((id) => api.delete(`/admin/ledgers/${id}`), {
    onSuccess: () => {
      message.success('Ledger deleted.');
      queryClient.invalidateQueries(['ledgers']);
    },
    onError: () => message.error('Failed to delete ledger.'),
  });

  let runningBalance = openingBalance;
  const entriesWithBalance = entries.map((entry) => {
    runningBalance = (runningBalance || 0) + (entry.debit || 0) - (entry.credit || 0);
    return { ...entry, _balance: runningBalance };
  });
  const totalDebit = entries.reduce((sum, e) => sum + (e.debit || 0), 0);
  const totalCredit = entries.reduce((sum, e) => sum + (e.credit || 0), 0);

  const openCreate = () => {
    setEditingLedger(null);
    setEntries([emptyRow(0)]);
    setOpeningBalance(0);
    form.resetFields();
    setIsDrawerVisible(true);
  };

  const openEdit = (ledger) => {
    setEditingLedger(ledger);
    setOpeningBalance(Number(ledger.opening_balance || 0));
    setEntries(
      (ledger.entries || []).map((entry) => ({
        key: entry.id,
        miti_bs: entry.miti_bs || '',
        date_ad: entry.date_ad ? dayjs(entry.date_ad) : null,
        vch_type: entry.vch_type || null,
        vch_no: entry.vch_no || '',
        particulars: entry.particulars || '',
        debit: Number(entry.debit || 0),
        credit: Number(entry.credit || 0),
      })),
    );
    form.resetFields();
    form.setFieldsValue({
      company_name: ledger.company_name,
      address: ledger.address,
      ledger_name: ledger.ledger_name,
      start_date: ledger.start_date ? dayjs(ledger.start_date) : null,
      end_date: ledger.end_date ? dayjs(ledger.end_date) : null,
      notes: ledger.notes,
    });
    setIsDrawerVisible(true);
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
    setEditingLedger(null);
    setEntries([]);
  };

  const addRow = () => {
    setEntries([...entries, emptyRow(entries.length)]);
  };

  const updateEntry = (index, field, value) => {
    setEntries(entries.map((e, i) => (i === index ? { ...e, [field]: value } : e)));
  };

  const removeEntry = (index) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        company_name: values.company_name,
        address: values.address,
        ledger_name: values.ledger_name,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : undefined,
        end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : undefined,
        opening_balance: Number(values.opening_balance || 0),
        notes: values.notes,
        entries: entries.map((e) => ({
          miti_bs: e.miti_bs || null,
          date_ad: e.date_ad ? e.date_ad.format('YYYY-MM-DD') : null,
          vch_type: e.vch_type || null,
          vch_no: e.vch_no || null,
          particulars: e.particulars || null,
          debit: Number(e.debit || 0),
          credit: Number(e.credit || 0),
        })),
      };

      if (editingLedger) {
        updateLedger.mutate({ id: editingLedger.id, payload });
      } else {
        createLedger.mutate(payload);
      }
    } catch {
      /* validation errors already shown */
    }
  };

  const downloadPdf = async (ledger) => {
    try {
      const response = await api.get(`/admin/ledgers/${ledger.id}/pdf/download`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Ledger-${ledger.company_name.replace(/[^a-zA-Z0-9]+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      message.error('Failed to download PDF.');
    }
  };

  const confirmDelete = (ledger) => {
    Modal.confirm({
      title: 'Delete Ledger',
      content: `Are you sure you want to delete the ledger for ${ledger.company_name}?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => deleteLedger.mutate(ledger.id),
    });
  };

  const columns = [
    {
      title: 'Company Name',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (value, record) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => openEdit(record)}>
          <strong>{value}</strong>
        </Button>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (value) => value || '-',
    },
    {
      title: 'Ledger Name',
      dataIndex: 'ledger_name',
      key: 'ledger_name',
      render: (value) => value || '-',
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => {
        const start = record.start_date ? dayjs(record.start_date).format('YYYY-MM-DD') : '...';
        const end = record.end_date ? dayjs(record.end_date).format('YYYY-MM-DD') : '...';
        return `${start} → ${end}`;
      },
    },
    {
      title: 'Entries',
      dataIndex: 'entries_count',
      key: 'entries_count',
      width: 80,
      align: 'center',
    },
    {
      title: 'Opening',
      dataIndex: 'opening_balance',
      key: 'opening_balance',
      align: 'right',
      render: (value) => (
        <Text>
          Rs. {Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: 'Closing',
      dataIndex: 'closing_balance',
      key: 'closing_balance',
      align: 'right',
      render: (value) => (
        <strong>
          Rs. {Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </strong>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 170,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit / View">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title="Download PDF">
            <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadPdf(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => confirmDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const entryColumns = [
    {
      title: 'Miti (BS)',
      key: 'miti_bs',
      width: 120,
      render: (_, record, index) => (
        <Input
          placeholder="e.g. 2082-06-25"
          value={record.miti_bs}
          onChange={(e) => updateEntry(index, 'miti_bs', e.target.value)}
        />
      ),
    },
    {
      title: 'Date (AD)',
      key: 'date_ad',
      width: 140,
      render: (_, record, index) => (
        <DatePicker
          style={{ width: '100%' }}
          value={record.date_ad}
          onChange={(value) => updateEntry(index, 'date_ad', value)}
        />
      ),
    },
    {
      title: 'Vch Type',
      key: 'vch_type',
      width: 130,
      render: (_, record, index) => (
        <Select
          allowClear
          showSearch
          style={{ width: '100%' }}
          placeholder="Vch Type"
          value={record.vch_type}
          onChange={(value) => updateEntry(index, 'vch_type', value)}
        >
          {VOUCHER_TYPES.map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Vch No.',
      key: 'vch_no',
      width: 90,
      render: (_, record, index) => (
        <Input
          value={record.vch_no}
          onChange={(e) => updateEntry(index, 'vch_no', e.target.value)}
        />
      ),
    },
    {
      title: 'Particulars',
      key: 'particulars',
      render: (_, record, index) => (
        <Input
          value={record.particulars}
          onChange={(e) => updateEntry(index, 'particulars', e.target.value)}
        />
      ),
    },
    {
      title: 'Debit (Dr)',
      key: 'debit',
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          min={0}
          precision={2}
          style={{ width: '100%' }}
          value={record.debit}
          placeholder="0.00"
          onChange={(value) => updateEntry(index, 'debit', value)}
        />
      ),
    },
    {
      title: 'Credit (Cr)',
      key: 'credit',
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          min={0}
          precision={2}
          style={{ width: '100%' }}
          value={record.credit}
          placeholder="0.00"
          onChange={(value) => updateEntry(index, 'credit', value)}
        />
      ),
    },
    {
      title: 'Balance',
      key: '_balance',
      width: 130,
      align: 'right',
      render: (_, record) => (
        <strong>
          Rs. {record._balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </strong>
      ),
    },
    {
      title: '',
      key: 'remove',
      width: 46,
      render: (_, __, index) => (
        <Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeEntry(index)} />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={4} style={{ marginBottom: 0 }}>
              <BookOutlined /> Ledger
            </Title>
            <Text type="secondary">
              Company ledgers with Miti (BS) / Date (AD) entries and running balance.
            </Text>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Ledger
            </Button>
          </Col>
        </Row>

        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            allowClear
            placeholder="Search by company name / ledger name / address"
            style={{ width: 300 }}
            onSearch={setSearchText}
          />
          <Button icon={<BookOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Space>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={Array.isArray(ledgersData?.data) ? ledgersData.data : []}
          pagination={{ pageSize: 10, showSizeChanger: true }}
        />
      </Card>

      <Drawer
        title={editingLedger ? `Edit Ledger — ${editingLedger.company_name}` : 'New Ledger'}
        width="95%"
        open={isDrawerVisible}
        onClose={closeDrawer}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button
              type="primary"
              loading={createLedger.isLoading || updateLedger.isLoading}
              onClick={handleSubmit}
            >
              {editingLedger ? 'Update Ledger' : 'Save Ledger'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Card size="small" title="Company Details" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="company_name"
                  label="Company Name"
                  rules={[{ required: true, message: 'Company name is required' }]}
                >
                  <Input placeholder="Company Pvt. Ltd." />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="address" label="Address">
                  <Input placeholder="Kathmandu, Nepal" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="ledger_name" label="Ledger Name">
                  <Input placeholder="e.g. Cash Book" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="start_date" label="Start Date (AD)">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="end_date" label="End Date (AD)">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="opening_balance" label="Opening Balance (Rs.)" initialValue={0}>
                  <InputNumber
                    min={0}
                    precision={2}
                    style={{ width: '100%' }}
                    onChange={(value) => setOpeningBalance(Number(value || 0))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="notes" label="Notes">
                  <Input placeholder="Optional notes about this ledger" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            size="small"
            title={
              <>
                Entries <Tag>Balance = Opening + Dr − Cr</Tag>
              </>
            }
            extra={
              <Button icon={<PlusOutlined />} onClick={addRow}>
                Add Row
              </Button>
            }
          >
            {entries.length === 0 ? (
              <Empty description="No entries yet" style={{ padding: '24px 0' }} />
            ) : (
              <>
                <Table
                  rowKey="key"
                  columns={entryColumns}
                  dataSource={entriesWithBalance}
                  pagination={false}
                  size="small"
                  scroll={{ x: 1100 }}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={5} align="right">
                        <strong>TOTAL</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <strong>
                          Rs. {totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <strong>
                          Rs. {totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <strong>
                          Rs.{' '}
                          {runningBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} />
                    </Table.Summary.Row>
                  )}
                />
                <Divider />
                <Row justify="end">
                  <Col>
                    <Text strong style={{ fontSize: 16 }}>
                      Closing Balance: Rs.{' '}
                      {runningBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Text>
                  </Col>
                </Row>
              </>
            )}
          </Card>
        </Form>
      </Drawer>
    </div>
  );
};

export default Ledgers;
